// A flow step's skeleton is a different node from the content that replaces it, and the UA SKIPS a view transition
// whose captured new element leaves the document mid-animation: a cold step "morphed" for ~40ms of its ~460ms.
// So the router's transitions capture their new state only once the named boxes hold real content.
// docs/notes/app/flow-view-transitions.md

const SKELETON_IN_A_NAMED_BOX = ':is(.flow-frame, .flow-header, .flow-content) [data-slot="skeleton"]'
// Rendering is frozen for as long as the update callback runs. ~20x the measured cold wait (~40ms), far below the
// UA's own 4s update-callback timeout; past it the skeleton is captured and the morph is lost, as before.
const CAPTURE_WAIT_CAP_MS = 800

const settled = () => document.querySelector(SKELETON_IN_A_NAMED_BOX) === null

function namedBoxesSettled(): Promise<void> {
  if (settled()) return Promise.resolve()
  return new Promise((resolve) => {
    const finish = () => {
      observer.disconnect()
      clearTimeout(cap)
      resolve()
    }
    const observer = new MutationObserver(() => {
      if (settled()) finish()
    })
    observer.observe(document.body, { childList: true, subtree: true })
    const cap = setTimeout(finish, CAPTURE_WAIT_CAP_MS)
  })
}

interface ViewTransitionRouter {
  startViewTransition: (fn: () => Promise<void>) => unknown
  shouldViewTransition?: unknown
  options: { defaultViewTransition?: unknown }
}

/** Holds the capture of each view transition the router starts until `namedBoxesSettled`. Client only. */
export function captureViewTransitionsSettled(router: ViewTransitionRouter): void {
  const start = router.startViewTransition
  router.startViewTransition = (update) => {
    // The router reads (and clears) this same flag inside `start`; a navigation with no transition must not be held.
    const transitions = Boolean(router.shouldViewTransition ?? router.options.defaultViewTransition)
    if (!transitions || typeof document.startViewTransition !== 'function') return start(update)
    return start(async () => {
      await update()
      await namedBoxesSettled()
    })
  }
}
